import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { deleteSimProduct, getSimProducts } from "../../../redux/slices/sims";
import NotyfContext from "../../../contexts/NotyfContext";
import Loader from "../../../components/spinners/Loader";
import simProductsData from "./simProductsData";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../../hooks/useWindowSize";
import { Helmet } from "react-helmet-async";
import {
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  defaultColumn,
} from "react-table";
import {
  Card,
  Col,
  Container,
  Row,
  Form,
  Pagination,
  Table,
  Button,
  DropdownButton,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaPencilAlt,
  FaPlus,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrashAlt,
} from "react-icons/fa";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import NewSimProduct from "./NewSimProduct";
import EditSimProduct from "./EditSimProduct";
import useDeviceType from "../../../hooks/useDeviceType";

const SimProducts = () => {
  const simProducts = useSelector((state) => state.sims.sim_products);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const notyf = useContext(NotyfContext);
  const { simProductsColumns } = useMemo(() => simProductsData(t), [t]);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const { backgroundColor, fontColor } = useWhitelabel();
  const [showNewSimProduct, setShowNewSimProduct] = useState(false);
  const [simProduct, setSimProduct] = useState(null);
  const [showEditSimProduct, setShowEditSimProduct] = useState(false);

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchSimProducts = async () => {
      await dispatch(getSimProducts())
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
        });
    };

    if (!!activeUser?.id) {
      fetchSimProducts();
    }
  }, [activeUser, dispatch, notyf]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: simProductsColumns,
      data: simProducts,
      initialState: { pageIndex: 0 },
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "Actions",
          sortable: false,
          Header: "Actions",
          Cell: ({ row }) => (
            <DropdownButton
              as={ButtonGroup}
              title="Actions"
              variant="primary"
              data-boundary="viewport"
              style={{
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
                border: "none",
              }}
            >
              <Dropdown.Item
                eventKey="edit"
                onClick={() => {
                  setSimProduct(row.original);
                  setShowEditSimProduct(true);
                }}
              >
                <FaPencilAlt />
                <span className="ms-2">Edit</span>
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="delete"
                onClick={async () => {
                  await dispatch(deleteSimProduct({ id: row.original.id }))
                    .then(() => {
                      notyf.open({
                        type: "success",
                        message: "Sim Product deleted successfully",
                        ripple: true,
                        dismissible: false,
                        duration: 5000,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    })
                    .catch((error) => {
                      notyf.open({
                        type: "danger",
                        message: error,
                        ripple: true,
                        dismissible: false,
                        duration: 5000,
                        position: {
                          x: "center",
                          y: "top",
                        },
                      });
                    });
                }}
              >
                <FaTrashAlt />
                <span className="ms-2">Delete</span>
              </Dropdown.Item>
            </DropdownButton>
          ),
        },
      ]);
    }
  );

  if (isLoading) {
    return <Loader />;
  }
  return (
    <React.Fragment>
      <Helmet title="SIM Products" />
      <Container fluid className="p-0">
        {isDesktop && (
          <div className="mb-3 d-flex justify-content-end align-items-center">
            <Button
              variant="primary"
              style={{
                border: "none",
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
              }}
              onClick={() => setShowNewSimProduct(true)}
            >
              <FaPlus />
              <span className="ms-2">New SIM Product</span>
            </Button>
          </div>
        )}
        {!isDesktop && (
          <div className="d-flex justify-content-center flex-column mb-3">
            <Button
              variant="primary"
              style={{
                border: "none",
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
                width: "100%",
                padding: "10px",
              }}
              onClick={() => setShowNewSimProduct(true)}
            >
              <FaPlus />
              <span className="ms-2">New SIM Product</span>
            </Button>
          </div>
        )}
        <Card>
          <Card.Header>
            <Card.Title>SIM Products</Card.Title>
          </Card.Header>
          <Card.Body>
            <Table id="invoicesTable" hover responsive {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        <span {...column.getSortByToggleProps()}>
                          {column.render("Header")}
                          <span>
                            {column.sortable ? (
                              column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FaSortUp className="ms-2 " />
                                ) : (
                                  <FaSortDown className="ms-2 " />
                                )
                              ) : (
                                <FaSort className="ms-2" />
                              )
                            ) : null}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {isDesktop && (
              <Row>
                <Col md="8">
                  <span className="mx-2">
                    {t("table.page")}{" "}
                    <strong>
                      {pageIndex + 1} {t("table.of")} {pageOptions.length}
                    </strong>
                  </span>
                  <span className="ms-3 me-2">{t("table.show")}:</span>
                  <Form.Select
                    className="d-inline-block w-auto"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </Form.Select>

                  <span className="ms-3 me-2">{t("table.goToPage")}:</span>
                  <Form.Control
                    className="d-inline-block"
                    type="number"
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      gotoPage(page);
                    }}
                    style={{ width: "75px" }}
                  />
                </Col>
                <Col md="4">
                  <Pagination className="float-end">
                    <Pagination.First
                      onClick={() => {
                        gotoPage(0);
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Prev
                      onClick={() => {
                        previousPage();
                      }}
                      disabled={!canPreviousPage}
                    />
                    <Pagination.Next
                      onClick={() => {
                        nextPage();
                      }}
                      disabled={!canNextPage}
                    />
                    <Pagination.Last
                      onClick={() => {
                        gotoPage(pageCount - 1);
                      }}
                      disabled={!canNextPage}
                    />
                  </Pagination>
                </Col>
              </Row>
            )}
            {!isDesktop && (
              <React.Fragment>
                <div className="invoices-pagination mt-3">
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-center">
                      <Pagination className="float-end">
                        <Pagination.Prev
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        />
                        <Col className="d-flex align-items-end mx-2 px-3">
                          <span className="mx-2">
                            <strong>
                              {pageIndex + 1} {t("table.of")}{" "}
                              {pageOptions.length}
                            </strong>
                          </span>
                        </Col>
                        <Pagination.Next
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        />
                      </Pagination>
                    </Col>
                  </Row>
                  <hr />
                  <Row>
                    <Col xs="auto">
                      <span className="me-2">{t("table.show")}:</span>
                      <Form.Select
                        className="d-inline-block w-auto"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                        }}
                        style={{
                          padding: "0.25rem 2.1rem 0.25rem 0.7rem",
                          border: "none",
                          boxShadow: "none",
                          fontSize: "14px",
                        }}
                      >
                        {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <div className="float-end">
                        <span className="me-2">{t("table.goToPage")}:</span>
                        <Form.Control
                          className="d-inline-block w-auto"
                          type="number"
                          defaultValue={pageIndex + 1}
                          min={1}
                          max={pageCount}
                          onChange={(e) => {
                            const page = e.target.value
                              ? Number(e.target.value) - 1
                              : 0;
                            gotoPage(page);
                          }}
                          style={{
                            padding: "0.25rem 1.2rem 0.25rem 0.7rem",
                            fontSize: "0.825rem",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            )}
            <NewSimProduct
              showNewSimProduct={showNewSimProduct}
              setShowNewSimProduct={setShowNewSimProduct}
            />
            <EditSimProduct
              showEditSimProduct={showEditSimProduct}
              setShowEditSimProduct={setShowEditSimProduct}
              simProduct={simProduct}
            />
          </Card.Body>
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default SimProducts;
